import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "../Layout/Layout";
import {
    useShowPageData,
    SHOW_LAYERS,
    VENUE_LAYERS,
    ROLE_KEY_BY_LAYER,
    COMPOSITIONS,
    VENUE_DESC_KEY_BY_LAYER,
    SLOT_GUIDANCE_KEY_BY_LAYER,
    GEO_PRESETS,
    useCompanyPlaylistsForPicker,
    useMediaLibraryForPicker,
    usePlaylistPublishImpact,
    useSwapLayerToPlaylist,
    useCreateLayerMediaPlaylist,
    useSwapLayerToMedia,
    useCompanyClocks,
    useCompanyWeathers,
    useCompanyRssFeeds,
    useCreateAndAttachRssFeed,
    useSetLayerAudioSource,
    useSetPlaygroundGeometry,
    useAspectRatiosForLayout,
    useCurrentCompositionRegions,
    useSwitchComposition,
    deriveMigratableRef,
    TILE_GEOMETRY,
    matchCompositionShape,
    REGION_ROLE_KEYS,
    REGION_GUIDANCE_KEYS,
    usePublishShowCampaign,
} from "../../queries/showPage";
import { useGetCompanyTags } from "../../queries";
import "./ShowPage.css";

// Phase 1 of the new Show Page (dev-only — see .claude/show-slots-prototype.html
// for the full spec). Scope, per the agreed build order:
//   1. Read existing shows, stacked composition only. Prove all 69 open. <- this file
//   2. Slot swapping + direct-media path
//   3. The audio question
//   4. Tiled compositions
// Nothing here writes anything yet — no swap, no publish, no composition change.
//
// If a future phase adds a control that repoints which show fills a venue
// "role" (e.g. "make this show the Morning show"), note before wiring it up:
// there is NO backend writer for that today. CafeWizardService::upsertShowSlot()
// is the only thing that writes venue_show_slots, and only from the café
// wizard's own build endpoints (/cafe/wizard/vibes, /cafe/wizard/match) — there
// is no generic "assign campaign X to role Y" endpoint. A control added here
// without a matching backend endpoint would silently do nothing, and the
// scene button resolving that role would keep pointing at the old show.

const ICON_BY_LAYER = { MAIN: "🖼️", PLAYGROUND: "▤", CORNER: "◳", BANNER: "▬" };

// Media library picker — every Media.type needs its OWN icon so a tile with
// no real thumbnail still reads as content, not as blank. AUDIO is not an
// edge case here: it's the common case for MAIN in this product, so it gets
// equal billing with IMAGE/VIDEO, not a generic fallback.
const MEDIA_ICON_BY_TYPE = { IMAGE: "🖼️", VIDEO: "🎬", AUDIO: "🎵", DEFAULT: "📄" };
// null = "الكل" (all) — the type-filter row's own real data (Media.type),
// standing in for the tag row playlists have and media doesn't.
const MEDIA_TYPE_FILTERS = [null, "IMAGE", "VIDEO", "AUDIO"];

// media.thumb is a complete, already-prefixed URL (see MakeImageThumb.php /
// MakeGLBThumb.php) — never construct it from media.path. Some rows still
// carry the old S3 bucket host from before the storage.popuppush.com CDN
// migration; LazyImage.jsx (the app's proven media-thumb component) rewrites
// those in place rather than trusting the stored value, so this mirrors that
// exact normalization instead of inventing a second, divergent one.
const OLD_S3_HOST_1 = "http://storage-popuppush-copy.s3.eu-west-1.amazonaws.com";
const OLD_S3_HOST_2 = "http://storage-popuppush.s3.me-south-1.amazonaws.com";
const CDN_HOST = "https://storage.popuppush.com";

function normalizeThumb(thumb) {
    if (!thumb || thumb === "loading") return null;
    if (thumb.includes(OLD_S3_HOST_1)) return thumb.replace(OLD_S3_HOST_1, CDN_HOST);
    if (thumb.includes(OLD_S3_HOST_2)) return thumb.replace(OLD_S3_HOST_2, CDN_HOST);
    return thumb;
}

function MediaThumb({ media }) {
    const [broken, setBroken] = useState(false);
    const src = normalizeThumb(media.thumb);
    useEffect(() => setBroken(false), [src]);
    if (!src || broken) {
        return MEDIA_ICON_BY_TYPE[media.type] || MEDIA_ICON_BY_TYPE.DEFAULT;
    }
    return <img src={src} alt="" onError={() => setBroken(true)} />;
}

// Ported verbatim from LoyaltyAnalytics.jsx's `fmt` helper.
function fmtNumber(n, lng) {
    try {
        return new Intl.NumberFormat(lng === "ar" ? "ar-EG" : "en-US").format(n);
    } catch {
        return String(n);
    }
}

// config/global.js's formatPublishedDate (and its duplicate in utils/format.js)
// hardcode Intl.DateTimeFormat("en-US", ...) — the date renders in English
// regardless of the active app language. That's a pre-existing, shared-utility
// bug also visible on CampaignSetup/PlaylistSetup/InteractiveCampaignSetup/
// LayoutSetup, all of which call the same function; fixing it there would
// change those four pages' output too, well beyond what this page needs, so
// it's reported separately rather than patched here. This is a local,
// ShowPage-only formatter with the same date-parsing behavior (see that
// file's history re: the naive-format/timezone bug) but locale-aware.
function formatPublishedDateLocalized(dateString, lng) {
    if (!dateString) return "";
    try {
        const raw = String(dateString);
        const date = raw.includes("T") ? new Date(raw) : new Date(`${raw.replace(" ", "T")}Z`);
        if (isNaN(date.getTime())) return raw;
        return new Intl.DateTimeFormat(lng === "ar" ? "ar-EG" : "en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }).format(date);
    } catch {
        return dateString;
    }
}

const AR_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
// mm:ss composed strings need digit substitution, not Intl.NumberFormat (which
// doesn't zero-pad or colon-join) — same technique the prototype's own dur() uses.
// The prototype's dur() only ever saw integer mock seconds; real durations are
// summed from custom_duration/default_duration, which can be fractional
// (e.g. 454.182313) — round to whole seconds first or `%60` and padStart leave
// the fraction sitting after the seconds, e.g. "7:34.182313...".
function formatDuration(totalSeconds, lng) {
    const total = Math.round(totalSeconds);
    const m = Math.floor(total / 60);
    const s = String(total % 60).padStart(2, "0");
    const str = `${m}:${s}`;
    return lng === "ar" ? str.replace(/\d/g, (d) => AR_DIGITS[d]) : str;
}

// A "multi" fill (more than one pivot row in the layer) has no single name —
// it's still real content, so it gets a count instead of being shown as empty.
// True whether or not those rows disagree (mixed); disagreement only changes
// whether the swap action is offered, never whether the slot reads as empty.
function fillDisplayName(fill, t, lng) {
    if (!fill) return null;
    if (fill.kind === "multi") {
        return t("show_page.slot_multi_fill_name", { count: fmtNumber(fill.count, lng) });
    }
    return fill.name;
}

// Overlay position — only meaningful for PLAYGROUND, in the stack composition,
// while it has a fill. Rendered per the prototype's GEO row, but inert: real
// per-show geometry (campaign_playlist_media.settings) isn't wired yet (see
// the note in Stage below).
const GEO_OPTIONS = ["full", "side", "box"];

// Which GEO_OPTIONS button (if any) matches a layer's actual saved geometry —
// compares against the exact values useSetPlaygroundGeometry itself writes
// (GEO_PRESETS, imported from queries/showPage.js — one source of truth for
// both the write and the read-back match, not two numbers that could drift).
// No stored geometry at all (a row that's never had this control touch it)
// reads as "full", matching CampaignContentObserver::defaultSettings() on
// the backend. A geometry that matches none of the three (set some other
// way, e.g. the detailed editor) shows no button pressed — real, custom
// state, not something this control should claim credit for.
function matchGeoPreset(geometry) {
    if (!geometry) return "full";
    const close = (a, b) => Math.abs((a ?? 0) - b) < 1;
    for (const id of GEO_OPTIONS) {
        const p = GEO_PRESETS[id];
        if (close(geometry.width, p.width) && close(geometry.height, p.height) &&
            close(geometry.top_margin, p.top_margin) && close(geometry.start_margin, p.start_margin)) {
            return id;
        }
    }
    return null;
}

// Ported verbatim from the prototype's buildPicker()/buildLists()/buildMedia()
// — .picker/.sp-p-head/.sp-p-title/.sp-p-sub/.sp-p-close/.sp-modes/.sp-mode/
// .sp-tags/.sp-p-tag/.sp-p-list/.sp-p-item/.sp-p-grid/.sp-cell/.sp-made/
// .sp-p-foot/.sp-p-count are the prototype's own classes (renamed with the
// sp- prefix; see ShowPage.css for exactly which two collided and why).
//
// ⚠️ ONE DELIBERATE INTERACTION CHANGE FROM THE PROTOTYPE: there, clicking a
// playlist in "من قوائمك" commits immediately (assign(s,p.id) on click). This
// version selects on click and requires a separate confirm — the blast-radius
// disclosure ("used in N other shows") has to be visible BEFORE the write, at
// the point of action, not as a toast after. An immediate-commit click has
// nowhere to put that. The media tab's foot-bar-confirm was already the
// prototype's own pattern for its tab; this makes both tabs consistent.
function Picker({ layer, slot, campaignId, campaignName, roleLabel: roleLabelProp, layoutId, t, lng, onClose }) {
    const [mode, setMode] = useState("lists");
    const [activeTag, setActiveTag] = useState(null);
    const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
    const [pickedMediaIds, setPickedMediaIds] = useState([]);
    const [mediaSearchInput, setMediaSearchInput] = useState("");
    const [mediaSearch, setMediaSearch] = useState("");
    const [mediaType, setMediaType] = useState(null);

    // Debounced — same 300ms figure the app's own AddContentSearchInput uses
    // elsewhere, so a real GET /media call fires per pause, not per keystroke.
    React.useEffect(() => {
        const id = setTimeout(() => setMediaSearch(mediaSearchInput.trim()), 300);
        return () => clearTimeout(id);
    }, [mediaSearchInput]);

    const tagsQ = useGetCompanyTags();
    const playlistsQ = useCompanyPlaylistsForPicker({
        per_page: 20,
        tags: activeTag ? [activeTag] : [],
    });
    const mediaQ = useMediaLibraryForPicker(layer, mediaSearch, mode === "media");
    const impactQ = usePlaylistPublishImpact(selectedPlaylistId);

    const swapToPlaylist = useSwapLayerToPlaylist();
    const createFromMedia = useCreateLayerMediaPlaylist();
    const busy = swapToPlaylist.isLoading || createFromMedia.isLoading;

    const playlists = (playlistsQ.data?.pages || []).flatMap((p) => p.items || []);
    const mediaItems = (mediaQ.data || []).filter(
        (m) => !m.is_folder && (!mediaType || m.type === mediaType)
    );
    // roleLabelProp is set by RegionSlot (a region isn't one of the 4 fixed
    // layers ROLE_KEY_BY_LAYER knows about — MAIN/PLAYGROUND/CORNER/BANNER —
    // so it passes its own composed label, e.g. "الربع ٣", directly).
    const roleLabel = roleLabelProp || t(`show_page.${ROLE_KEY_BY_LAYER[layer]}`);

    const confirmNone = () => {
        swapToPlaylist.mutate(
            { campaignId, layer, playlistId: null, oldRowIds: slot.rowIds, layoutId },
            { onSuccess: onClose }
        );
    };
    // "بدون محتوى" / "إزالة هذا العنصر" — clear the layer, no replacement.
    // Reuses the swap mutation with no playlistId: the assign POST is simply
    // skipped when there's nothing to add (see useSwapLayerToPlaylist).
    const confirmPlaylist = () => {
        if (!selectedPlaylistId) return;
        swapToPlaylist.mutate(
            { campaignId, layer, playlistId: selectedPlaylistId, oldRowIds: slot.rowIds, layoutId },
            { onSuccess: onClose }
        );
    };
    const confirmMedia = () => {
        if (!pickedMediaIds.length) return;
        createFromMedia.mutate(
            { campaignId, layer, mediaIds: pickedMediaIds, roleLabel, layoutId },
            { onSuccess: onClose }
        );
    };

    return (
        <div className="sp-picker">
            <div className="sp-p-head">
                <div>
                    <div className="sp-p-title">{roleLabel}</div>
                    <div className="sp-p-sub">{t("show_page.picker_sub")}</div>
                </div>
                <button type="button" className="sp-p-close" onClick={onClose} disabled={busy}>
                    {t("show_page.picker_close")}
                </button>
            </div>
            {/* Same suggestion as the slot card, repeated here because THIS is
                where the choice actually gets made — a line the user already
                scrolled past on the card doesn't help at the moment of picking. */}
            <div className="sp-p-guide">{t(`show_page.${SLOT_GUIDANCE_KEY_BY_LAYER[layer]}`)}</div>

            <div className="sp-modes">
                <button
                    type="button"
                    className="sp-mode"
                    aria-pressed={mode === "lists"}
                    onClick={() => setMode("lists")}
                >
                    {t("show_page.picker_mode_lists")}
                </button>
                <button
                    type="button"
                    className="sp-mode"
                    aria-pressed={mode === "media"}
                    onClick={() => setMode("media")}
                >
                    {t("show_page.picker_mode_media")}
                </button>
            </div>

            {mode === "lists" ? (
                <>
                    {tagsQ.data?.length > 0 && (
                        <div className="sp-tags">
                            <button
                                type="button"
                                className="sp-p-tag"
                                aria-pressed={activeTag === null}
                                onClick={() => setActiveTag(null)}
                            >
                                {t("show_page.picker_tag_all")}
                            </button>
                            {tagsQ.data.map((tag) => (
                                <button
                                    key={tag.id}
                                    type="button"
                                    className="sp-p-tag"
                                    aria-pressed={activeTag === tag.id}
                                    onClick={() => setActiveTag(tag.id)}
                                >
                                    {tag.name}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="sp-p-list">
                        <button
                            type="button"
                            className="sp-p-item sp-p-none"
                            aria-pressed={selectedPlaylistId === "__none__"}
                            onClick={() => setSelectedPlaylistId("__none__")}
                        >
                            <span>
                                <span className="sp-nm">{t("show_page.picker_none_show")}</span>
                                <span className="sp-mt">{t("show_page.picker_none_show_hint")}</span>
                            </span>
                        </button>

                        {playlistsQ.isLoading && <div className="sp-p-sub">{t("show_page.picker_loading")}</div>}
                        {!playlistsQ.isLoading && playlists.length === 0 && (
                            <div className="sp-p-sub">{t("show_page.picker_empty_playlists")}</div>
                        )}
                        {playlists.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                className="sp-p-item"
                                aria-pressed={selectedPlaylistId === p.id}
                                onClick={() => setSelectedPlaylistId(p.id)}
                            >
                                <span>
                                    <span className="sp-nm">{p.name}</span>
                                    <span className="sp-mt">
                                        {(p.tags_list || []).map((tg) => tg.name).join(", ")}
                                        {typeof p.media?.length === "number"
                                            ? ` ${p.tags_list?.length ? "· " : ""}${t("show_page.slot_item_count", { count: fmtNumber(p.media.length, lng) })}`
                                            : ""}
                                    </span>
                                </span>
                                {p.id === slot.fill?.id && (
                                    <span className="sp-cur">{t("show_page.picker_currently_published")}</span>
                                )}
                            </button>
                        ))}
                        {playlistsQ.hasNextPage && (
                            <button type="button" className="sp-p-item" onClick={() => playlistsQ.fetchNextPage()}>
                                <span className="sp-nm">…</span>
                            </button>
                        )}
                    </div>

                    {selectedPlaylistId && selectedPlaylistId !== "__none__" && (
                        <div className="sp-p-foot" style={{ flexWrap: "wrap" }}>
                            {impactQ.data?.campaign_count > 0 && (
                                <div className="sp-p-impact">
                                    {t("show_page.picker_impact", {
                                        campaigns: fmtNumber(impactQ.data.campaign_count, lng),
                                        devices: fmtNumber(impactQ.data.device_count, lng),
                                    })}
                                </div>
                            )}
                            <button type="button" className="sp-btn sp-btn-go sp-p-go" disabled={busy} onClick={confirmPlaylist}>
                                {slot.fill ? t("show_page.action_swap") : t("show_page.action_add")}
                            </button>
                        </div>
                    )}
                    {selectedPlaylistId === "__none__" && (
                        <div className="sp-p-foot">
                            <button type="button" className="sp-btn sp-btn-go sp-p-go" disabled={busy} onClick={confirmNone}>
                                {t("show_page.action_swap")}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <input
                        type="text"
                        className="sp-p-search"
                        placeholder={t("show_page.picker_media_search_placeholder")}
                        value={mediaSearchInput}
                        onChange={(e) => setMediaSearchInput(e.target.value)}
                    />
                    {/* Media has no tags relationship in this backend (only Playlist
                        does) — reusing .sp-tags/.sp-p-tag's shape on the one real
                        filterable field media DOES have (type), not borrowed/fake tags. */}
                    <div className="sp-tags">
                        {MEDIA_TYPE_FILTERS.map((mt) => (
                            <button
                                key={mt || "all"}
                                type="button"
                                className="sp-p-tag"
                                aria-pressed={mediaType === mt}
                                onClick={() => setMediaType(mt)}
                            >
                                {t(`show_page.picker_media_type_${mt ? mt.toLowerCase() : "all"}`)}
                            </button>
                        ))}
                    </div>
                    <div className="sp-p-grid">
                        {/* getLayerMedia() returns a raw response()->json(['data'=>...]) —
                            unlike most endpoints here, NOT wrapped in CompanyResource. The
                            query fn (useMediaLibraryForPicker) already unwraps that one
                            layer, so mediaQ.data IS the array — unwrapping again as
                            mediaQ.data?.data silently produced [] always, which is why
                            this tab read as broken regardless of what was in the library. */}
                        {mediaQ.isLoading && <div className="sp-p-sub">{t("show_page.picker_loading")}</div>}
                        {!mediaQ.isLoading && mediaItems.length === 0 && (
                            <div className="sp-p-sub">{t("show_page.picker_empty_media")}</div>
                        )}
                        {mediaItems.map((m) => {
                            const idx = pickedMediaIds.indexOf(m.id);
                            const picked = idx > -1;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    className="sp-cell"
                                    aria-pressed={picked}
                                    onClick={() =>
                                        setPickedMediaIds((cur) =>
                                            picked ? cur.filter((id) => id !== m.id) : [...cur, m.id]
                                        )
                                    }
                                >
                                    {/* background toggle + two-line caption ported from the
                                        prototype's own cell markup (thumb bg swaps on
                                        selection; name/kind stacked via a line break). Every
                                        type gets its OWN icon — AUDIO is the common case for
                                        MAIN in this product, not an edge case, and it was
                                        silently falling into the image icon before this. */}
                                    <div className="sp-thumb" style={{ background: picked ? "var(--sp-brand-soft)" : "var(--sp-page)" }}>
                                        <MediaThumb media={m} />
                                    </div>
                                    <div className="sp-cap">
                                        {m.file_name}
                                        <br />
                                        <span style={{ color: "var(--sp-ink-mute)" }}>{m.type}</span>
                                    </div>
                                    {picked && <span className="sp-ord">{fmtNumber(idx + 1, lng)}</span>}
                                </button>
                            );
                        })}
                    </div>

                    <div className="sp-made">
                        {pickedMediaIds.length > 0
                            ? t("show_page.picker_made_with_picks", {
                                  name: `${roleLabel} · ${formatPublishedDateLocalized(new Date().toISOString(), lng)}`,
                              })
                            : t("show_page.picker_made_no_picks")}
                    </div>

                    <div className="sp-p-foot">
                        <span className="sp-p-count">
                            {pickedMediaIds.length > 0
                                ? t("show_page.picker_count_selected", { count: fmtNumber(pickedMediaIds.length, lng) })
                                : t("show_page.picker_count_none")}
                        </span>
                        <button
                            type="button"
                            className="sp-btn sp-btn-go sp-p-go"
                            disabled={busy || !pickedMediaIds.length}
                            onClick={confirmMedia}
                        >
                            {t("show_page.picker_add")}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

// CORNER (logo) and BANNER (clock/weather) — a single direct-media
// assignment, not a playlist. BANNER's source is Clock/Weather media
// specifically (GET /clock/all, /weather/all — confirmed these ARE Media
// rows, media.type CLOCK/WEATHER, attached through the exact same
// POST /campaign/content/assign, type:"media" call CORNER uses — see
// useSwapLayerToMedia's own comment). One component, one write path,
// branching only on where the candidate list comes from.
function VenuePicker({ layer, slot, campaignId, t, onClose }) {
    const isBanner = layer === "BANNER";
    // ⚠️ WIDENED 2026-09-03: the bar renders exactly what the player already
    // supports (see docs research this session) — clock/weather were never
    // the ceiling, they were just all this tab offered. "text" stays listed
    // even though nothing backs it yet (no freeform-text Media type exists
    // anywhere in the pipeline) — house rule: a missing option must say why,
    // not vanish. Not applicable to CORNER: its media grid (the `else`
    // branch below) already accepts IMAGE incl. GIF with zero restriction.
    const [widgetTab, setWidgetTab] = useState("clock");
    const [pickedId, setPickedId] = useState(null);
    const [rssUrl, setRssUrl] = useState("");
    const [rssLabel, setRssLabel] = useState("");

    const mediaQ = useMediaLibraryForPicker(layer, "", !isBanner || widgetTab === "image");
    const clocksQ = useCompanyClocks(isBanner && widgetTab === "clock");
    const weathersQ = useCompanyWeathers(isBanner && widgetTab === "weather");
    const rssFeedsQ = useCompanyRssFeeds("rss", isBanner && widgetTab === "rss");
    const rssCustomQ = useCompanyRssFeeds("rss_custom", isBanner && widgetTab === "rss");

    const swapToMedia = useSwapLayerToMedia();
    const createRss = useCreateAndAttachRssFeed();
    const busy = swapToMedia.isLoading || createRss.isLoading;
    const roleLabel = t(`show_page.${ROLE_KEY_BY_LAYER[layer]}`);

    const widgetQ = widgetTab === "clock" ? clocksQ : widgetTab === "weather" ? weathersQ : null;
    const featureDisabled = widgetQ?.error?.response?.status === 402;
    const widgetItems = featureDisabled ? [] : widgetQ?.data || [];
    const mediaItems = (mediaQ.data || []).filter((m) => !m.is_folder);
    const rssFeatureDisabled =
        rssFeedsQ.error?.response?.status === 402 || rssCustomQ.error?.response?.status === 402;
    const rssItems = rssFeatureDisabled ? [] : [...(rssFeedsQ.data || []), ...(rssCustomQ.data || [])];
    const rssLoading = rssFeedsQ.isLoading || rssCustomQ.isLoading;

    const confirmNone = () => {
        swapToMedia.mutate({ campaignId, layer, mediaId: null, oldRowIds: slot.rowIds }, { onSuccess: onClose });
    };
    const confirm = () => {
        if (!pickedId) return;
        swapToMedia.mutate({ campaignId, layer, mediaId: pickedId, oldRowIds: slot.rowIds }, { onSuccess: onClose });
    };
    const submitNewRss = () => {
        if (!rssUrl.trim() || createRss.isLoading) return;
        createRss.mutate(
            { campaignId, layer, url: rssUrl.trim(), label: rssLabel.trim(), oldRowIds: slot.rowIds },
            { onSuccess: onClose }
        );
    };

    return (
        <div className="sp-picker">
            <div className="sp-p-head">
                <div>
                    <div className="sp-p-title">{roleLabel}</div>
                    <div className="sp-p-sub">{t("show_page.picker_sub")}</div>
                </div>
                <button type="button" className="sp-p-close" onClick={onClose} disabled={busy}>
                    {t("show_page.picker_close")}
                </button>
            </div>
            <div className="sp-p-guide">{t(`show_page.${SLOT_GUIDANCE_KEY_BY_LAYER[layer]}`)}</div>

            {isBanner && (
                <div className="sp-modes">
                    {["clock", "weather", "image", "rss", "text"].map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            className="sp-mode"
                            aria-pressed={widgetTab === tab}
                            onClick={() => {
                                setWidgetTab(tab);
                                setPickedId(null);
                            }}
                        >
                            {t(`show_page.venue_picker_mode_${tab}`)}
                        </button>
                    ))}
                </div>
            )}

            {isBanner && widgetTab === "text" ? (
                // No freeform-text Media type exists anywhere in the content
                // pipeline (checked: not Media, not campaign_playlist_media,
                // not any BANNER endpoint) — shown, not hidden, disabled with
                // the reason rather than silently absent.
                <div className="sp-p-sub">{t("show_page.venue_picker_text_unavailable")}</div>
            ) : isBanner && widgetTab === "rss" ? (
                <>
                    <div className="sp-p-list">
                        <button
                            type="button"
                            className="sp-p-item sp-p-none"
                            aria-pressed={pickedId === "__none__"}
                            onClick={() => setPickedId("__none__")}
                        >
                            <span>
                                <span className="sp-nm">{t("show_page.picker_none_venue")}</span>
                                <span className="sp-mt">{t("show_page.picker_none_venue_hint")}</span>
                            </span>
                        </button>
                        {rssLoading && <div className="sp-p-sub">{t("show_page.picker_loading")}</div>}
                        {rssFeatureDisabled && (
                            <div className="sp-p-sub">{t("show_page.venue_picker_feature_disabled_rss")}</div>
                        )}
                        {!rssLoading && !rssFeatureDisabled && rssItems.length === 0 && (
                            <div className="sp-p-sub">{t("show_page.venue_picker_empty_rss")}</div>
                        )}
                        {rssItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className="sp-p-item"
                                aria-pressed={pickedId === item.id}
                                onClick={() => setPickedId(item.id)}
                            >
                                <span>
                                    <span className="sp-nm">{item.file_name}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                    {!rssFeatureDisabled && (
                        <div className="sp-rss-add">
                            <div className="sp-p-sub">{t("show_page.venue_rss_add_hint")}</div>
                            <input
                                type="text"
                                className="sp-p-search"
                                placeholder={t("show_page.venue_rss_label_placeholder")}
                                value={rssLabel}
                                onChange={(e) => setRssLabel(e.target.value)}
                            />
                            <input
                                type="text"
                                className="sp-p-search"
                                placeholder={t("show_page.venue_rss_url_placeholder")}
                                value={rssUrl}
                                onChange={(e) => setRssUrl(e.target.value)}
                            />
                            <button
                                type="button"
                                className="sp-btn sp-btn-go"
                                disabled={!rssUrl.trim() || busy}
                                onClick={submitNewRss}
                            >
                                {t("show_page.venue_rss_add_action")}
                            </button>
                            {createRss.isError && (
                                <div className="sp-p-sub">{t("show_page.venue_rss_add_error")}</div>
                            )}
                        </div>
                    )}
                </>
            ) : isBanner && widgetTab !== "image" ? (
                <div className="sp-p-list">
                    <button
                        type="button"
                        className="sp-p-item sp-p-none"
                        aria-pressed={pickedId === "__none__"}
                        onClick={() => setPickedId("__none__")}
                    >
                        <span>
                            <span className="sp-nm">{t("show_page.picker_none_venue")}</span>
                            <span className="sp-mt">{t("show_page.picker_none_venue_hint")}</span>
                        </span>
                    </button>
                    {widgetQ?.isLoading && <div className="sp-p-sub">{t("show_page.picker_loading")}</div>}
                    {featureDisabled && (
                        <div className="sp-p-sub">{t(`show_page.venue_picker_feature_disabled_${widgetTab}`)}</div>
                    )}
                    {!widgetQ?.isLoading && !featureDisabled && widgetItems.length === 0 && (
                        <div className="sp-p-sub">{t(`show_page.venue_picker_empty_${widgetTab}`)}</div>
                    )}
                    {widgetItems.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className="sp-p-item"
                            aria-pressed={pickedId === item.id}
                            onClick={() => setPickedId(item.id)}
                        >
                            <span>
                                <span className="sp-nm">
                                    {item.clock?.title || item.weather?.title || item.title || item.file_name}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="sp-p-grid">
                    {mediaQ.isLoading && <div className="sp-p-sub">{t("show_page.picker_loading")}</div>}
                    {!mediaQ.isLoading && mediaItems.length === 0 && (
                        <div className="sp-p-sub">{t("show_page.picker_empty_media")}</div>
                    )}
                    {/* Parity with the BANNER list's own "إزالة هذا العنصر" row —
                        only offered when there's something to remove, same as the
                        show-layer Picker's "بدون محتوى" only appearing there too. */}
                    {slot.fill && (
                        <button
                            type="button"
                            className="sp-cell sp-p-none"
                            aria-pressed={pickedId === "__none__"}
                            onClick={() => setPickedId("__none__")}
                        >
                            <div className="sp-thumb" style={{ background: "var(--sp-page)" }}>✕</div>
                            <div className="sp-cap">{t("show_page.picker_none_venue")}</div>
                        </button>
                    )}
                    {mediaItems.map((m) => (
                        <button
                            key={m.id}
                            type="button"
                            className="sp-cell"
                            aria-pressed={pickedId === m.id}
                            onClick={() => setPickedId(m.id)}
                        >
                            <div className="sp-thumb" style={{ background: pickedId === m.id ? "var(--sp-brand-soft)" : "var(--sp-page)" }}>
                                <MediaThumb media={m} />
                            </div>
                            <div className="sp-cap">{m.file_name}</div>
                        </button>
                    ))}
                </div>
            )}

            <div className="sp-p-foot">
                <button
                    type="button"
                    className="sp-btn sp-btn-go sp-p-go"
                    disabled={busy || !pickedId}
                    onClick={pickedId === "__none__" ? confirmNone : confirm}
                >
                    {slot.fill ? t("show_page.action_swap") : t("show_page.action_add")}
                </button>
            </div>
        </div>
    );
}

function Slot({ layer, slot, campaignId, campaignName, t, lng, isOpen, onOpen, onClose, mainIsLayout }) {
    const roleLabel = t(`show_page.${ROLE_KEY_BY_LAYER[layer]}`);
    const fillName = fillDisplayName(slot.fill, t, lng);
    const setGeo = useSetPlaygroundGeometry();
    const currentGeo = layer === "PLAYGROUND" ? matchGeoPreset(slot.geometry) : null;
    // MAIN holding a layout (tiled composition) isn't editable through this
    // page's own content picker — per-cell content is the detailed editor's
    // job ("do not expose nested layouts"). Getting OUT of a layout is the
    // composition control's job (see CompositionPicker's "طبقات فوق بعض"
    // option), not this slot's own تبديل button.
    //
    // PLAYGROUND is blocked the same way whenever MAIN is a layout — not
    // because PLAYGROUND itself holds one, but because composition switching
    // guarantees PLAYGROUND's own row is empty in that state (its content, if
    // any, migrated into the layout's region 2 — see useSwitchComposition).
    // Letting the normal picker add something here anyway would recreate the
    // exact "does an overlay coexist with a tiled MAIN" ambiguity the whole
    // region-migration design exists to avoid — device-side compositing
    // behavior for that combination isn't verified anywhere in this repo.
    const blockedByLayout = slot.fill?.kind === "layout" || (layer === "PLAYGROUND" && mainIsLayout);

    // Picker REPLACES the slot's own card in place, same as the prototype's
    // buildPicker() vs buildSlot() branch — never nested inside it. Guarded
    // here too, not just by the caller: a mixed layer's swap button doesn't
    // exist (renders sp-s-link instead), so isOpen should never be true for
    // one, but this makes it structurally impossible regardless of caller.
    if (isOpen && !slot.mixed && !blockedByLayout) {
        // VENUE_LAYERS (CORNER logo, BANNER clock/weather) are fixed single
        // widgets, not playlists — "من قوائمك" doesn't apply, and BANNER's
        // source is Clock/Weather media, not the general library. Branching
        // HERE (not inside Picker) so each component only ever calls its own
        // fixed set of hooks — Picker's own lists/media queries would fire
        // unwanted requests for a venue slot, and branching inside Picker
        // after some of its hooks had already run would violate the rules of
        // hooks the moment layer ever changed under one mounted instance.
        return VENUE_LAYERS.includes(layer) ? (
            <VenuePicker layer={layer} slot={slot} campaignId={campaignId} t={t} onClose={onClose} />
        ) : (
            <Picker
                layer={layer}
                slot={slot}
                campaignId={campaignId}
                campaignName={campaignName}
                t={t}
                lng={lng}
                onClose={onClose}
            />
        );
    }

    return (
        <div className={`sp-slot${slot.mixed ? " mixed" : ""}`}>
            <div className="sp-s-key">{ICON_BY_LAYER[layer]}</div>
            <div className="sp-s-body">
                <div className="sp-s-role">{roleLabel}</div>
                {/* Always on, filled or not — the prototype this was ported from
                    only rendered it on an empty slot, and every real show's
                    slots are full, so it never actually appeared. A pure
                    suggestion (never disables/blocks the action button below),
                    text sourced from SLOT_GUIDANCE_KEY_BY_LAYER + locale, not
                    hardcoded per layer here. */}
                <div className="sp-s-guide">{t(`show_page.${SLOT_GUIDANCE_KEY_BY_LAYER[layer]}`)}</div>
                <div className={`sp-s-fill${fillName ? "" : " none"}`}>
                    {fillName || t("show_page.slot_empty")}
                </div>
                {slot.fill?.kind === "playlist" && typeof slot.itemCount === "number" && (
                    <div className="sp-s-meta">
                        {t("show_page.slot_item_count", { count: fmtNumber(slot.itemCount, lng) })}
                        {slot.durationSeconds > 0 ? ` · ${formatDuration(slot.durationSeconds, lng)}` : ""}
                    </div>
                )}
                {/* Mixed slots stay readable (name/meta above still render) — only the
                    swap action is replaced, with a link out instead of a button, so this
                    page can never flatten a show whose items deliberately differ. */}
                {slot.mixed && <div className="sp-s-warn">{t("show_page.slot_mixed_warning")}</div>}
                {/* Skipped for a mixed layer: this fans the same geometry out to
                    EVERY row in it (see useSetPlaygroundGeometry), which would
                    flatten rows that deliberately differ — exactly what "mixed
                    layers stay blocked" exists to prevent. slot.mixed already
                    explains why the swap action is blocked above; extending
                    that silently here rather than repeating the warning. */}
                {layer === "PLAYGROUND" && slot.fill && !slot.mixed && (
                    <div className="sp-geo">
                        <span className="sp-geo-lbl">{t("show_page.geo_label")}</span>
                        {GEO_OPTIONS.map((g) => (
                            <button
                                key={g}
                                type="button"
                                disabled={setGeo.isLoading}
                                aria-pressed={g === currentGeo}
                                onClick={() => setGeo.mutate({ campaignId, geo: g, rowIds: slot.rowIds })}
                            >
                                {t(`show_page.geo_${g}`)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {slot.mixed ? (
                <Link className="sp-s-link" to={`/campaigns/${campaignId}/setup`}>
                    {t("show_page.open_detailed_editor")}
                </Link>
            ) : blockedByLayout ? (
                <span className="sp-s-hint">
                    {t(slot.fill?.kind === "layout" ? "show_page.layout_fill_hint" : "show_page.playground_blocked_by_layout_hint")}
                </span>
            ) : (
                <button type="button" className="sp-s-act" onClick={onOpen}>
                    {slot.fill ? t("show_page.action_swap") : t("show_page.action_add")}
                </button>
            )}
        </div>
    );
}

// A tiled composition's own content section — ported from the prototype's
// COMPS[id].slots: the COMPOSITION decides which slots exist, not a fixed
// pair. "طبقات فوق بعض" keeps rendering the existing two (Slot, above,
// unchanged); any tiled composition renders one RegionSlot per region
// instead, each opening the SAME Picker used everywhere else on this page —
// just pointed at that region's own screen campaign_id (layer is always
// "MAIN" there; a screen IS a whole small campaign, and this page only ever
// touches its own MAIN, same as "do not expose nested layouts" already
// scoped this feature). No new write path: useSwapLayerToPlaylist/
// useCreateLayerMediaPlaylist already take campaignId+layer as plain
// parameters — a region is just a different pair of values for the same
// calls, with layoutId threaded through so the composition-regions cache
// (shared with the Stage preview) invalidates correctly afterward.
function RegionSlot({ region, index, shapeId, layoutId, campaignId, t, lng, isOpen, onOpen, onClose }) {
    const roleKey = REGION_ROLE_KEYS[shapeId]?.[index];
    const guidanceKey = REGION_GUIDANCE_KEYS[shapeId]?.[index] || "guidance_region_any";
    // Falls back to a plain ordinal when the geometry doesn't match one of
    // the three named presets (a layout built some other way) — never blank.
    const roleLabel = roleKey ? t(`show_page.${roleKey}`) : t("show_page.region_generic", { n: fmtNumber(index + 1, lng) });
    const fillName = fillDisplayName(region.fill, t, lng);
    const slot = { fill: region.fill, rowIds: region.rowIds };

    if (isOpen) {
        return (
            <Picker
                layer="MAIN"
                slot={slot}
                campaignId={region.screenCampaignId}
                roleLabel={roleLabel}
                layoutId={layoutId}
                t={t}
                lng={lng}
                onClose={onClose}
            />
        );
    }

    return (
        <div className="sp-slot">
            <div className="sp-s-key">▧</div>
            <div className="sp-s-body">
                <div className="sp-s-role">{roleLabel}</div>
                <div className="sp-s-guide">{t(`show_page.${guidanceKey}`)}</div>
                <div className={`sp-s-fill${fillName ? "" : " none"}`}>{fillName || t("show_page.slot_empty")}</div>
            </div>
            <button type="button" className="sp-s-act" onClick={onOpen}>
                {region.fill ? t("show_page.action_swap") : t("show_page.action_add")}
            </button>
        </div>
    );
}

// Composition switching REGION-MIGRATES real content, both directions —
// background→region 1, overlay→region 2 (and back), whichever regions the
// target actually has. CORNER/BANNER are never touched here at all: they're
// venue-persistent ("ثابت في المكان"), read/written on the TOP-level
// campaign's own CORNER/BANNER layers regardless of what MAIN points at —
// composition switching only ever reads/writes MAIN, PLAYGROUND, and (for a
// tiled source/target) a layout's own screens' MAIN layer. Nothing here
// reaches CORNER/BANNER, so there's nothing to preserve on purpose — they
// were simply never in scope for this control to touch.
//
// A warning only appears when a region ACTUALLY has content that doesn't fit
// the target (fewer target regions than source regions with something in
// them) — named by its real playlist/media name, not a generic caution. The
// common cases (stack↔halves/mainside, and quarters' extra regions starting
// empty) need no warning at all, per design.
function CompositionPicker({ t, lng, campaignId, campaignName, mainSlot, pgSlot, isCurrentlyTiled, regionsQ }) {
    const [pending, setPending] = useState(null);
    // regionsQ is fetched once in the parent (ShowPage) and shared with
    // Stage — both need "what's actually in the current tiled composition
    // right now."
    const sourceRegionsLoading = isCurrentlyTiled && regionsQ.isLoading;
    // Stack source needs no fetch — MAIN's and PLAYGROUND's rows are already
    // in hand from useShowPageData. A tiled source's regions come from the
    // query above, in screen order.
    const sourceRegions = isCurrentlyTiled
        ? (regionsQ.data || []).map((r) => r.ref)
        : [deriveMigratableRef(mainSlot.rawRows), deriveMigratableRef(pgSlot.rawRows)];
    // Same index space as sourceRegions (0=MAIN/region0, 1=PLAYGROUND/region1,
    // …) — which one, if any, is CURRENTLY the sole audible source. Carried
    // into the switch so the chosen source survives it the same way content
    // does; -1 (findIndex's own "none" value) already means what "fall back
    // to صامت" needs it to mean, no extra translation.
    const isAudible = (rows) => rows && rows.length > 0 && rows.every((r) => !Number(r.is_muted));
    const oldAudioIndex = isCurrentlyTiled
        ? (regionsQ.data || []).findIndex((r) => isAudible(r.rawRows))
        : [isAudible(mainSlot.rawRows), isAudible(pgSlot.rawRows)].findIndex(Boolean);

    // Checked up front, not gated on a tile click — a company without the
    // "layouts" feature (or one with zero aspect ratios seeded) previously
    // only found that out AFTER tapping a tile, and the confirm button just
    // sat disabled forever with no explanation. Known before the tiles even
    // render now, so the tiles themselves can say why.
    const aspectRatiosQ = useAspectRatiosForLayout();
    const featureDisabled = aspectRatiosQ.error?.response?.status === 402;
    // ⚠️ FIXED — this used to be `featureDisabled || (!isLoading && !isError
    // && !data?.length)`: the second clause required !isError, so ANY error
    // that wasn't specifically a 402 (a 404, a 500, a network failure — this
    // is exactly what the dead /layout/aspect-ratios route produced) matched
    // NEITHER branch and left tiledUnavailable false — tiles stayed
    // clickable, no reason ever rendered, while the confirm button's own
    // separate (and correct) `!data?.length` check still locked it forever.
    // That gap reproduced the exact bug this control exists to prevent, in
    // the one case it didn't cover. Now: loading is the only state that
    // doesn't immediately resolve to "unavailable, say why."
    const tiledUnavailable = !aspectRatiosQ.isLoading && (aspectRatiosQ.isError || !aspectRatiosQ.data?.length);
    const tiledUnavailableReason = featureDisabled
        ? t("show_page.composition_feature_disabled")
        : aspectRatiosQ.isError
        ? t("show_page.composition_aspect_ratios_error")
        : t("show_page.composition_no_aspect_ratios");

    const switchComposition = useSwitchComposition();
    const busy = switchComposition.isLoading;

    // ⚠️ FIXED — this was `isCurrentlyTiled ? null : "stack"` from BEFORE
    // matchCompositionShape existed, when there genuinely was no way to know
    // which tile was active. matchCompositionShape (built later, for Stage's
    // grid ratios and RegionSlot's role labels) already answers this exact
    // question from the same regionsQ data this component already has — it
    // just never got wired in here too. Not a missing feature, a stale
    // default that predated the fix elsewhere.
    const current = isCurrentlyTiled ? (regionsQ.data ? matchCompositionShape(regionsQ.data) : null) : "stack";

    const targetCount = pending === "stack" ? 2 : TILE_GEOMETRY[pending]?.length || 0;
    const migrating = [];
    const lost = [];
    if (pending) {
        sourceRegions.forEach((ref, i) => {
            if (!ref) return;
            if (i < targetCount && !ref.unmigratable) migrating.push({ ref, targetIndex: i });
            else lost.push(ref);
        });
    }

    const confirm = () => {
        if (!pending) return;
        const aspectRatioId = pending === "stack" ? null : aspectRatiosQ.data?.[0]?.id;
        if (pending !== "stack" && !aspectRatioId) return;
        switchComposition.mutate(
            {
                campaignId,
                campaignName,
                targetCompositionId: pending,
                aspectRatioId,
                migrating,
                oldMainRowIds: mainSlot.rowIds,
                oldPlaygroundRowIds: pgSlot.rowIds,
                oldAudioIndex,
            },
            { onSuccess: () => setPending(null) }
        );
    };

    return (
        <>
            <div className="sp-comp-g">
                {COMPOSITIONS.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        className="sp-cp"
                        disabled={busy || (c.kind !== "stack" && (aspectRatiosQ.isLoading || tiledUnavailable))}
                        aria-pressed={c.id === current}
                        onClick={() => c.id !== current && setPending(c.id)}
                    >
                        {c.kind === "stack" ? (
                            <div className="sp-dia">
                                <span className="sp-st1" />
                                <span className="sp-st2" />
                            </div>
                        ) : (
                            <div
                                className="sp-dia"
                                style={{ gridTemplateColumns: c.cols, gridTemplateRows: c.rows }}
                            >
                                {Array.from({ length: c.cells }).map((_, i) => (
                                    <i key={i} />
                                ))}
                            </div>
                        )}
                        <div className="sp-lb">{t(`show_page.${c.labelKey}`)}</div>
                    </button>
                ))}
            </div>
            {!pending && tiledUnavailable && <div className="sp-comp-hint">{tiledUnavailableReason}</div>}
            {pending && (
                <div className={`sp-comp-confirm${lost.length ? " warn" : ""}`}>
                    {sourceRegionsLoading ? (
                        <div>{t("show_page.composition_confirm_checking")}</div>
                    ) : (
                        <>
                            <div>
                                {lost.length > 0
                                    ? t("show_page.composition_confirm_lost", { names: lost.map((r) => r.displayName).join("، ") })
                                    : t("show_page.composition_confirm_neutral")}
                            </div>
                            <div className="sp-comp-confirm-note">{t("show_page.composition_confirm_venue_unaffected")}</div>
                        </>
                    )}
                    <div className="sp-comp-confirm-actions">
                        <button type="button" onClick={() => setPending(null)} disabled={busy}>
                            {t("show_page.composition_confirm_cancel")}
                        </button>
                        <button
                            type="button"
                            className="sp-btn sp-btn-go"
                            onClick={confirm}
                            disabled={busy || sourceRegionsLoading || (pending !== "stack" && (tiledUnavailable || !aspectRatiosQ.data?.length))}
                        >
                            {t("show_page.composition_confirm_go")}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

// The audio question (ported from the prototype's .aud row) — which layer's
// sound plays. Writes campaign_playlist_media.is_muted per row, fanned out
// across a whole layer (useSetLayerAudioSource) — the per-row field the
// classic builder's own item-level mute toggle already uses, NOT the
// separate campaigns.settings.live.layer_settings bulk override (left
// untouched deliberately, see that hook's own comment).
// ⚠️ FIXED — audio now follows the composition the same way content slots
// do: stacked offers الخلفية/فوقها/صامت (unchanged); a tiled composition
// offers one option per REGION instead — those are the slots that actually
// exist there, and "الخلفية"/"فوقها" don't. Left unfixed, a real gap was
// hiding underneath: nothing in the backend, the classic editor, or the
// player enforces a single audio source — two unmuted regions genuinely
// both play sound. That was already true in principle for MAIN/PLAYGROUND;
// it becomes concretely likely the moment there are 4 regions instead of 2,
// which is exactly why this control exists at all.
function AudioRow({ t, lng, campaignId, slots, isCurrentlyTiled, regionsQ, layoutId, shapeId }) {
    const setAudio = useSetLayerAudioSource();

    if (isCurrentlyTiled) {
        const regions = regionsQ.data || [];
        const loading = regionsQ.isLoading || !regionsQ.data;
        const groups = regions.map((r) => ({ key: r.screenCampaignId, rowIds: r.rowIds }));
        const audible = regions.map((r) => r.rawRows?.length > 0 && r.rawRows.every((row) => !Number(row.is_muted)));
        const audibleCount = audible.filter(Boolean).length;
        let current = null;
        if (audibleCount === 1) current = groups[audible.indexOf(true)].key;
        else if (audibleCount === 0 && regions.some((r) => r.rawRows?.length > 0)) current = "silent";
        const nothingToPlay = regions.every((r) => !r.rawRows?.length);

        const choose = (key) => setAudio.mutate({ campaignId, groups, chosenKey: key, layoutId });

        return (
            <div className="sp-aud">
                <span className="sp-aud-lb">{t("show_page.audio_label")}</span>
                <div className="sp-aud-opts">
                    {loading ? (
                        <span className="sp-aud-hint">{t("show_page.picker_loading")}</span>
                    ) : (
                        <>
                            {regions.map((r, i) => {
                                const roleKey = REGION_ROLE_KEYS[shapeId]?.[i];
                                const label = roleKey ? t(`show_page.${roleKey}`) : t("show_page.region_generic", { n: fmtNumber(i + 1, lng) });
                                return (
                                    <button
                                        key={r.screenCampaignId}
                                        type="button"
                                        disabled={setAudio.isLoading || !r.rawRows?.length}
                                        aria-pressed={current === r.screenCampaignId}
                                        onClick={() => choose(r.screenCampaignId)}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                            <button
                                type="button"
                                disabled={setAudio.isLoading || nothingToPlay}
                                aria-pressed={current === "silent"}
                                onClick={() => choose(null)}
                            >
                                {t("show_page.audio_silent")}
                            </button>
                        </>
                    )}
                </div>
                {!loading && nothingToPlay && <div className="sp-aud-hint">{t("show_page.audio_nothing_hint")}</div>}
            </div>
        );
    }

    const main = slots.MAIN;
    const pg = slots.PLAYGROUND;
    // Same reasoning as the GEO guard: useSetLayerAudioSource ALWAYS fans
    // is_muted out across both MAIN's and PLAYGROUND's rows at once (muting
    // whichever isn't chosen) — so every choice, not just "silent," touches
    // both layers. If either is mixed, any click here would flatten rows
    // that deliberately differ, which "mixed layers stay blocked" forbids.
    const anyMixed = main.mixed || pg.mixed;

    // "Audible" = every row in that layer unmuted; a layer with zero rows is
    // neither audible nor mutable, so it can't be the source. Both layers
    // showing audible at once shouldn't happen once this control is the only
    // writer, but a pre-existing row set from elsewhere could — that reads as
    // no button pressed (real, ambiguous state) rather than a guessed one.
    const mainAudible = main.rowCount > 0 && main.audibleRowCount === main.rowCount;
    const pgAudible = pg.rowCount > 0 && pg.audibleRowCount === pg.rowCount;
    let current = null;
    if (mainAudible && !pgAudible) current = "MAIN";
    else if (pgAudible && !mainAudible) current = "PLAYGROUND";
    else if (!mainAudible && !pgAudible && (main.rowCount > 0 || pg.rowCount > 0)) current = "silent";

    const nothingToPlay = main.rowCount === 0 && pg.rowCount === 0;

    const groups = [
        { key: "MAIN", rowIds: main.rowIds },
        { key: "PLAYGROUND", rowIds: pg.rowIds },
    ];
    const choose = (key) => setAudio.mutate({ campaignId, groups, chosenKey: key });

    return (
        <div className="sp-aud">
            <span className="sp-aud-lb">{t("show_page.audio_label")}</span>
            <div className="sp-aud-opts">
                {SHOW_LAYERS.map((layer) => (
                    <button
                        key={layer}
                        type="button"
                        disabled={setAudio.isLoading || slots[layer].rowCount === 0 || anyMixed}
                        aria-pressed={current === layer}
                        onClick={() => choose(layer)}
                    >
                        {t(`show_page.${ROLE_KEY_BY_LAYER[layer]}`)}
                    </button>
                ))}
                <button
                    type="button"
                    disabled={setAudio.isLoading || nothingToPlay || anyMixed}
                    aria-pressed={current === "silent"}
                    onClick={() => choose(null)}
                >
                    {t("show_page.audio_silent")}
                </button>
            </div>
            {nothingToPlay && <div className="sp-aud-hint">{t("show_page.audio_nothing_hint")}</div>}
            {!nothingToPlay && anyMixed && <div className="sp-aud-hint">{t("show_page.audio_mixed_hint")}</div>}
        </div>
    );
}

// One still frame standing in for "how this looks on screen" — never
// playback. AUDIO (and any fill with no thumb — e.g. a playlist whose first
// item hasn't finished processing) gets its type icon instead of a blank
// box: audio in the background is the normal case for MAIN in this product,
// not a degraded one, so it needs to read as deliberate, not broken.
function frameBackgroundStyle(thumb) {
    const src = normalizeThumb(thumb);
    if (!src) return {};
    return {
        backgroundImage: `linear-gradient(140deg, rgba(58,46,99,.55), rgba(34,27,61,.72)), url("${src}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
    };
}

// The overlay renders AT ITS ACTUAL GEOMETRY now that useSetPlaygroundGeometry
// is a real write (see GEO_PRESETS/matchGeoPreset above) — not always
// full-bleed. Reads the raw saved percentages directly (not just the 3 preset
// ids), so a geometry set some other way (the detailed editor) still
// positions correctly here, not just the ones this page's own GEO row offers.
// No stored geometry (never touched) falls back to the same full-bleed
// default CampaignContentObserver::defaultSettings() gives a fresh row.
function overlayPositionStyle(geometry) {
    const g = geometry || GEO_PRESETS.full;
    return {
        top: `${g.top_margin ?? 0}%`,
        insetInlineStart: `${g.start_margin ?? 0}%`,
        width: `${g.width ?? 100}%`,
        height: `${g.height ?? 100}%`,
    };
}

function Stage({ slots, t, lng, isTiled, compositionRegions, compositionLoading }) {
    const bg = slots.MAIN;
    const ov = slots.PLAYGROUND;
    const corner = slots.CORNER;
    const banner = slots.BANNER;
    const bgThumb = normalizeThumb(bg.previewThumb);
    const ovThumb = normalizeThumb(ov.previewThumb);
    const cornerThumb = normalizeThumb(corner.previewThumb);

    // A full-bleed overlay (the only geo this phase renders — see the note
    // below) isn't offset from the background at all: .sp-over.full and
    // .sp-z-full are both `inset:0` on the same frame, both centered — the
    // *same box*, not two boxes side by side. Measured it: with both
    // rendering text, .sp-z-full's name/sub landed within a few px of
    // .sp-over's tag/name, reading as one garbled overlapping block. The
    // prototype's own hint text already says what should happen instead —
    // "الطبقة العليا تغطّي الخلفية بالكامل — تُسمَع الخلفية ولا تُرى" ("the
    // top layer covers the background completely — heard, not seen") — so
    // the covered background keeps its gradient (still visible as a
    // backdrop through the overlay's translucent scrim) but stops drawing
    // its own name/count, which is what was actually colliding.
    const covered = Boolean(bg.fill && ov.fill);

    // Tiled composition — ported from the prototype's own paintFrame() else
    // branch: a .tiles grid (grid-template-columns/rows = the matched
    // preset's own c.cols/c.rows, e.g. "1fr 1fr" for halves) with one .tile
    // per region. The prototype only ever put a name in each tile; thumbnails
    // are this page's own later addition (see MediaThumb/frameBackgroundStyle
    // above), so tiles get the SAME thumb-or-icon-or-name treatment
    // .sp-z-full/.sp-over already use, not new logic.
    const shapeId = isTiled && compositionRegions ? matchCompositionShape(compositionRegions) : null;
    const shapeDef = COMPOSITIONS.find((c) => c.id === shapeId);
    const tilesStyle = shapeDef
        ? { gridTemplateColumns: shapeDef.cols, gridTemplateRows: shapeDef.rows }
        : { gridTemplateColumns: `repeat(${compositionRegions?.length || 1}, 1fr)`, gridTemplateRows: "1fr" };

    return (
        <div className="sp-frame">
            {isTiled ? (
                compositionLoading || !compositionRegions ? (
                    <div className="sp-z-full">
                        <div className="sp-nm">{t("show_page.stage_composition_loading")}</div>
                    </div>
                ) : (
                    <div className="sp-tiles" style={tilesStyle}>
                        {compositionRegions.map((r, i) => {
                            const thumb = normalizeThumb(r.previewThumb);
                            return (
                                <div
                                    key={r.screenCampaignId || i}
                                    className={`sp-tile${r.fill ? "" : " blank"}`}
                                    style={r.fill ? frameBackgroundStyle(r.previewThumb) : undefined}
                                >
                                    {r.fill ? (
                                        <div>
                                            {!thumb && (
                                                <div className="sp-z-icon sp-z-icon-sm">
                                                    {MEDIA_ICON_BY_TYPE[r.previewType] || MEDIA_ICON_BY_TYPE.DEFAULT}
                                                </div>
                                            )}
                                            <span className="sp-t">{fillDisplayName(r.fill, t, lng)}</span>
                                        </div>
                                    ) : (
                                        <span className="sp-t">{t("show_page.tile_blank")}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )
            ) : (
                <>
                    <div className={`sp-z-full${bg.fill ? "" : " empty"}`} style={bg.fill ? frameBackgroundStyle(bg.previewThumb) : undefined}>
                        {bg.fill ? (
                            !covered && (
                                <div>
                                    {!bgThumb && (
                                        <div className="sp-z-icon">
                                            {MEDIA_ICON_BY_TYPE[bg.previewType] || MEDIA_ICON_BY_TYPE.DEFAULT}
                                        </div>
                                    )}
                                    <div className="sp-nm">{fillDisplayName(bg.fill, t, lng)}</div>
                                    {bg.fill.kind === "playlist" && typeof bg.itemCount === "number" && (
                                        <div className="sp-sub">
                                            {t("show_page.slot_item_count", { count: fmtNumber(bg.itemCount, lng) })}
                                            {bg.durationSeconds > 0 ? ` · ${formatDuration(bg.durationSeconds, lng)}` : ""}
                                        </div>
                                    )}
                                </div>
                            )
                        ) : (
                            <div className="sp-nm">{t("show_page.stage_empty_background")}</div>
                        )}
                    </div>
                    {covered && (
                        <div
                            className={`sp-over${matchGeoPreset(ov.geometry) === "full" || !ov.geometry ? " full" : " boxed"}`}
                            style={{ ...overlayPositionStyle(ov.geometry), ...frameBackgroundStyle(ov.previewThumb) }}
                        >
                            <div>
                                {!ovThumb && (
                                    <div className="sp-z-icon sp-z-icon-sm">
                                        {MEDIA_ICON_BY_TYPE[ov.previewType] || MEDIA_ICON_BY_TYPE.DEFAULT}
                                    </div>
                                )}
                                <span className="sp-tag">{t("show_page.role_playground")}</span>
                                <span className="sp-nm">{fillDisplayName(ov.fill, t, lng)}</span>
                            </div>
                        </div>
                    )}
                </>
            )}
            {/* Venue-persistent — renders on top of EVERY composition, stacked
                or tiled alike. The page's own copy already promises this
                ("يظهر في كل العروض ومع كل تركيب"); nothing above this point
                should ever suppress it. */}
            {corner.fill && (
                <div className="sp-corner">
                    {cornerThumb && <img src={cornerThumb} alt="" />}
                    {fillDisplayName(corner.fill, t, lng)}
                </div>
            )}
            {banner.fill && (
                <div className="sp-bar">
                    <span className="sp-mk">▬</span>
                    <span>{fillDisplayName(banner.fill, t, lng)}</span>
                </div>
            )}
        </div>
    );
}

export default function ShowPage() {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const lng = i18n.language?.split("-")[0] || "en";
    const { isLoading, isError, campaign, slots } = useShowPageData(id);
    const [openLayer, setOpenLayer] = useState(null);
    const publishCampaign = usePublishShowCampaign();
    // Fetched once here (not inside Stage or CompositionPicker separately) —
    // both need "what's actually in the current tiled composition right
    // now," and react-query caches by [key, layoutId], so one fetch serves
    // both. Safe to call before the loading/error early returns below:
    // slots.MAIN is always a real object (fill:null while loading), never
    // undefined, so this never throws before campaign data resolves.
    const isCurrentlyTiled = slots.MAIN.fill?.kind === "layout";
    const layoutRegionsQ = useCurrentCompositionRegions(isCurrentlyTiled ? slots.MAIN.fill.id : null);
    // Which named preset's role labels ("القسم الأيمن" etc.) apply to the
    // content section below — same match Stage uses for its own grid ratios,
    // computed once here too since RegionSlot needs the shape id, not just
    // the geometry.
    const contentShapeId = isCurrentlyTiled && layoutRegionsQ.data ? matchCompositionShape(layoutRegionsQ.data) : null;

    if (isLoading) {
        return (
            <Layout padding={false}>
                <div className="show-page">
                    <div className="sp-shell sp-loading">{t("show_page.loading")}</div>
                </div>
            </Layout>
        );
    }
    if (isError || !campaign) {
        return (
            <Layout padding={false}>
                <div className="show-page">
                    <div className="sp-shell sp-error">{t("show_page.load_error")}</div>
                </div>
            </Layout>
        );
    }

    const hasChanges = campaign.has_unpublished_changes === true;

    return (
        <Layout padding={false}>
        <div className="show-page">
            <div className="sp-shell">
                <div className="sp-head">
                    <div className="sp-avatar">🖥️</div>
                    <div>
                        <div className="sp-h-name">{campaign.name}</div>
                        <div className="sp-h-meta">
                            {campaign.neverPublished
                                ? t("show.show_not_yet_published")
                                : t("show.show_last_published_on", {
                                      date: campaign.last_published
                                          ? formatPublishedDateLocalized(campaign.last_published, lng)
                                          : t("show.show_unknown_date"),
                                  })}
                        </div>
                    </div>
                </div>

                <div className="sp-cols">
                    <div className="sp-rail">
                        <div className="sp-stage">
                            <Stage
                                slots={slots}
                                t={t}
                                lng={lng}
                                isTiled={isCurrentlyTiled}
                                compositionRegions={layoutRegionsQ.data}
                                compositionLoading={isCurrentlyTiled && layoutRegionsQ.isLoading}
                            />
                            <div className="sp-stage-note">{t("show_page.stage_note")}</div>
                        </div>
                        <div className="sp-state">
                            <span className={`sp-pill ${hasChanges ? "dirty" : "ok"}`}>
                                <span className="sp-dot" />
                                {hasChanges ? t("shows.unpublished_changes") : t("shows.published")}
                            </span>
                            {/* Draft-only writes (see useSwapLayerToPlaylist /
                                useCreateLayerMediaPlaylist) — this is the ONLY thing
                                that makes a swap reach a screen. Same endpoint
                                CampaignSetup.jsx's own Publish button calls. */}
                            <button
                                type="button"
                                className="sp-btn sp-btn-go"
                                disabled={!hasChanges || publishCampaign.isLoading}
                                onClick={() => publishCampaign.mutate(id)}
                            >
                                {t("show_page.publish")}
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="sp-comp">
                            <div className="sp-comp-h">
                                <span className="sp-comp-t">{t("show_page.composition_title")}</span>
                                <span className="sp-comp-n">{t("show_page.composition_subtitle")}</span>
                            </div>
                            <CompositionPicker
                                t={t}
                                lng={lng}
                                campaignId={id}
                                campaignName={campaign.name}
                                mainSlot={slots.MAIN}
                                pgSlot={slots.PLAYGROUND}
                                isCurrentlyTiled={isCurrentlyTiled}
                                regionsQ={layoutRegionsQ}
                            />
                        </div>

                        <AudioRow
                            t={t}
                            lng={lng}
                            campaignId={id}
                            slots={slots}
                            isCurrentlyTiled={isCurrentlyTiled}
                            regionsQ={layoutRegionsQ}
                            layoutId={isCurrentlyTiled ? slots.MAIN.fill.id : null}
                            shapeId={contentShapeId}
                        />

                        <div className="sp-sec">
                            <div className="sp-sec-head">
                                <span className="sp-sec-title">{t("show_page.content_section_title")}</span>
                                <span className="sp-sec-note">{t("show_page.content_section_note")}</span>
                            </div>
                            {isCurrentlyTiled ? (
                                layoutRegionsQ.isLoading || !layoutRegionsQ.data ? (
                                    <div className="sp-p-sub">{t("show_page.picker_loading")}</div>
                                ) : (
                                    layoutRegionsQ.data.map((region, i) => (
                                        <RegionSlot
                                            key={region.screenCampaignId}
                                            region={region}
                                            index={i}
                                            shapeId={contentShapeId}
                                            layoutId={slots.MAIN.fill.id}
                                            campaignId={id}
                                            t={t}
                                            lng={lng}
                                            isOpen={openLayer === region.screenCampaignId}
                                            onOpen={() => setOpenLayer(region.screenCampaignId)}
                                            onClose={() => setOpenLayer(null)}
                                        />
                                    ))
                                )
                            ) : (
                                SHOW_LAYERS.map((layer) => (
                                    <Slot
                                        key={layer}
                                        layer={layer}
                                        slot={slots[layer]}
                                        campaignId={id}
                                        campaignName={campaign.name}
                                        t={t}
                                        lng={lng}
                                        isOpen={openLayer === layer}
                                        onOpen={() => setOpenLayer(layer)}
                                        onClose={() => setOpenLayer(null)}
                                        mainIsLayout={false}
                                    />
                                ))
                            )}
                        </div>

                        <div className="sp-sec persist">
                            <div className="sp-sec-head">
                                <span className="sp-sec-title">{t("show_page.venue_section_title")}</span>
                                <span className="sp-sec-note">
                                    {VENUE_LAYERS.some((layer) => slots[layer].fill)
                                        ? t("show_page.venue_section_note_active")
                                        : t("show_page.venue_section_note_empty")}
                                </span>
                            </div>
                            {VENUE_LAYERS.filter((layer) => slots[layer].fill).map((layer) => (
                                <Slot
                                    key={layer}
                                    layer={layer}
                                    slot={slots[layer]}
                                    campaignId={id}
                                    campaignName={campaign.name}
                                    t={t}
                                    lng={lng}
                                    isOpen={openLayer === layer}
                                    onOpen={() => setOpenLayer(layer)}
                                    onClose={() => setOpenLayer(null)}
                                />
                            ))}
                            {VENUE_LAYERS.filter((layer) => !slots[layer].fill && openLayer === layer).map((layer) => (
                                <VenuePicker
                                    key={layer}
                                    layer={layer}
                                    slot={slots[layer]}
                                    campaignId={id}
                                    t={t}
                                    onClose={() => setOpenLayer(null)}
                                />
                            ))}
                            {VENUE_LAYERS.some((layer) => !slots[layer].fill && openLayer !== layer) && (
                                <div className="sp-addv">
                                    {VENUE_LAYERS.filter((layer) => !slots[layer].fill && openLayer !== layer).map((layer) => (
                                        <button key={layer} type="button" onClick={() => setOpenLayer(layer)}>
                                            <span>+</span>
                                            <span>{t(`show_page.${VENUE_DESC_KEY_BY_LAYER[layer]}`)}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </Layout>
    );
}
